<script setup lang="ts">
import { computed, ref, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useCurrentLocale } from '@/plugins/i18n';
import { useQuasar } from 'quasar';
import { storeToRefs } from 'pinia';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';

import BackButton from '@/components/BackButton.vue';
import FormActions from '@/components/FormActions.vue';
import DynamicTabs from '@/components/DynamicTabs.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import MailingCampaignTestEmailDialog from '@/components/dialogs/MailingCampaignTestEmailDialog.vue';
import MailingCampaignSendConfirmationDialog from '@/components/dialogs/MailingCampaignSendConfirmationDialog.vue';
import MailingListModal from '@/components/MailingListModal.vue';

import { useCommunityStore, useMailingCampaignStore, useMailingListStore } from '@/stores';
import type { MailingCampaignDto, ProjectDto } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { useRouteNumberParam } from '@/composables/use-route-param';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { getLanguageEnabled, getTranslation, provideEditLocale } from '@/composables/data-display-utils';
import { formSaveNavigationGuard, hasRouteChanged, resetFormToItem } from '@/helpers/form.helper';
import { localizableString } from '@/helpers/validation.helper';

// CONST
const { t } = useI18n();
const $q = useQuasar();
const locale = useCurrentLocale();

// REFS
const selectedMailingList = ref();

// STORE
const communityStore = useCommunityStore();
const mailingCampaignStore = useMailingCampaignStore();
const mailingListStore = useMailingListStore();

const campaignId = useRouteNumberParam('id');

const { community } = storeToRefs(communityStore);

const languageEnabled = computed(() => getLanguageEnabled(community.value));

// FORM
const validationSchema = computed(() =>
  yup.object<ProjectDto>({
    title: localizableString(languageEnabled.value),
    subject: localizableString(languageEnabled.value),
    mailing_list_id: yup.number().required(),
    html_body: localizableString(languageEnabled.value),
    status: yup.number().required(),
  }),
);
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, resetForm, setFieldValue, values } = formContext;
const languageErrors = useLanguageValidationErrors(errors);

const campaign = useWatchItemLoading<MailingCampaignDto>(campaignId, mailingCampaignStore, (item) => {
  resetFormToItem(formContext, item);
});

const editLocale = provideEditLocale(languageEnabled);

// COMPUTED
const isDirty = computed(() => {
  return unref(meta).dirty;
});

const tabs = computed(() => {
  return [{ id: 'infos', name: t('campaign.mailingLists.tabs.infos'), isDirty: isDirty.value, icon: 'o_info' }];
});
const statusOptions = computed(() => mailingCampaignStore.getStatuses());

const openedTab = computed(() => {
  if (window.location.hash) {
    return window.location.hash.substring(1);
  }
  return 'infos';
});

const breadcrumbs = computed(() => {
  return [
    {
      label: campaign.value?.title,
      to: { name: 'mailing_campaign_detail', params: { id: campaign.value?.id } },
      icon: 'o_folder_copy',
    },
  ];
});

// LIFECYCLE
watch(
  campaignId,
  async (id) => {
    await mailingListStore.fetchMailingLists();
    if (id < 0) return;
    await mailingCampaignStore.fetchMailingCampaign(id);
    setInitialMailingList();
  },
  { immediate: true },
);

// ROUTER
onBeforeRouteLeave(formSaveNavigationGuard(meta));
onBeforeRouteUpdate((to, from, next) => {
  if (!hasRouteChanged(to, from)) {
    return formSaveNavigationGuard(meta)(to, from, next);
  }
  next();
});

// FUNCTIONS
const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await mailingCampaignStore.saveMailingCampaign(values as MailingCampaignDto),
    () => resetForm({ values: campaign.value! }),
  );
});

const onCancel = () => {
  if (campaign.value) {
    resetForm({ values: campaign.value });
    setInitialMailingList();
  } else {
    console.warn('onCancel: campaign is null');
  }
};

const emailTestDialog = () => {
  $q.dialog({
    component: MailingCampaignTestEmailDialog,
    componentProps: {
      campaign_id: campaignId.value,
    },
  });
};

const campaignSentConfirmationDialog = () => {
  const mailingList = mailingListStore.getItem(campaign.value?.mailing_list_id);
  $q.dialog({
    component: MailingCampaignSendConfirmationDialog,
    componentProps: {
      mailing_list_recipient: mailingList?.membersCount,
    },
  }).onOk(() => {
    mailingCampaignStore.sendCampaign(campaignId.value);
  });
};

const openMailingListModal = () => {
  $q.dialog({
    component: MailingListModal,
    componentProps: {
      title: t('campaign.dialogs.modal.title'),
      languageEnabled: languageEnabled.value,
    },
  }).onOk((value) => {
    const mailingListData = {
      id: value.id,
      title: value?.title_parent,
      parent: value?.parent,
      type: value?.type,
    };
    selectedMailingList.value = mapItemToLabelValue(mailingListData);
    setFieldValue('mailing_list_id', value.id);
  });
};

function mapItemToLabelValue(item: { id?: number; title?: any; parent: number; type: number }) {
  if (!item.id || !item.title) return null;
  const category = mailingListStore.getMailingListCategory(item.parent, item.type);
  return {
    label: `${category}${t('global.colon')} ${getTranslation(item, 'title', languageEnabled.value)}`,
    value: item.id,
  };
}

function setInitialMailingList() {
  const mailingList = mailingListStore.mailingLists.find((ml) => ml.id === values.mailing_list_id);
  if (!mailingList?.title_parent) return;

  selectedMailingList.value = mapItemToLabelValue({
    id: mailingList.id,
    title: mailingList.title_parent,
    parent: mailingList.parent,
    type: mailingList.type,
  });
}

function updateMailingListName() {
  const mailingList = mailingListStore.mailingLists.find((ml) => ml.id === selectedMailingList.value?.value);
  if (!mailingList?.title_parent) return;

  selectedMailingList.value = mapItemToLabelValue({
    id: mailingList.id,
    title: mailingList.title_parent,
    parent: mailingList.parent,
    type: mailingList.type,
  });
}

// WATCH
watch(
  () => editLocale.value,
  () => updateMailingListName(),
);
watch(
  () => locale.value,
  () => updateMailingListName(),
);
</script>

<template>
  <q-page class="campaign-detail">
    <div class="navigation">
      <breadcrumbs :elements="breadcrumbs" :language-enabled="languageEnabled" />
      <back-button to="/mailing-campaign" />
    </div>
    <div class="title">
      <div>
        <q-icon name="o_folder_copy" />
        <h1>{{ getTranslation(campaign, 'title', languageEnabled) }}</h1>
      </div>
    </div>
    <div v-if="campaign">
      <dynamic-tabs :tabs="tabs" :active="openedTab" keep-alive>
        <template #infos>
          <form-actions
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            v-model:editLocale="editLocale"
            :languageEnabled="languageEnabled"
          />
          <localized-input
            name="title"
            :label="t('campaign.title')"
            :editLocale="editLocale"
            outlined
            :language_enabled="languageEnabled"
          />
          <localized-select
            name="mailing_list_id"
            :label="t('campaign.mailingLists.label')"
            :editLocale="editLocale"
            :options="[]"
            emit-value
            map-options
            outlined
            disable
            v-model="selectedMailingList"
            @click="openMailingListModal"
          />
          <localized-input
            name="subject"
            :label="t('campaign.subject')"
            :editLocale="editLocale"
            outlined
            :language_enabled="languageEnabled"
          />
          <q-field :label="t('global.label.content')" stack-label color="dark" borderless>
            <template v-slot:default>
              <localized-editor
                name="html_body"
                :label="t('global.label.content')"
                class="q-mt-sm full-width"
                :editLocale="editLocale"
                :language_enabled="languageEnabled"
                disable-sanitize-html
              />
            </template>
          </q-field>
          <br />
          <localized-select
            name="status"
            :label="t('campaign.status.label')"
            :editLocale="editLocale"
            :options="statusOptions"
            emit-value
            map-options
            outlined
            disabled
          />

          <hr />
          <div class="actions">
            <q-btn color="primary" :label="t('campaign.action.testEmail')" @click="emailTestDialog" />

            <q-btn
              color="secondary"
              :label="t('campaign.action.sendCampaign')"
              @click="campaignSentConfirmationDialog"
            />
          </div>
        </template>
      </dynamic-tabs>
    </div>
  </q-page>
</template>
<style lang="scss">
.campaign-detail {
  padding: 16px;

  .navigation {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0 8px;
  }

  .title {
    margin: 20px 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0 10px;
    gap: 10px;

    .q-btn {
      max-height: 36px;
    }

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      max-width: 80%;

      h1 {
        display: block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .q-icon {
        font-size: $large-title;
      }
    }
  }

  .information-general {
    padding: 10px;

    &__header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 10px 0;

      .title {
        color: $color-primary;
        font-size: $small-title;
      }
    }
  }
  .actions {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
  }
}
</style>
